import { useAppSelector } from "app/hooks";
import { useCallback, useEffect, useState } from "react";
import { notNullNorUndefined } from "shared/helpers/notNullNorUndefined";
import MeasureApi from "./measureApi";
import { useAppDispatch } from "app/hooks";
import { setMeasureMode } from "features/krpano/details-slice";
import { RootState } from "app/store";
import { MeasurePoint } from "types/sharedTypes";
import { addKrpanoHotspot, addKrpanoHotspotLayer } from "shared/helpers/krpano";

type MeasuredResult =
  | {
      message: string;
    }
  | {
      result: number;
    }
  | null;

type UseMeasure = (
  measureMode: RootState["krpano"]["measureMode"]
) => [MeasuredResult, VoidFunction];

/**
 * Listens for the `krpano__get_measure` event and calculates the measured
 * distance using the points sent
 */
export const useMeasure: UseMeasure = (measureMode) => {
  const [measuredResult, setMeasuredResult] = useState<MeasuredResult>(null);

  const dispatch = useAppDispatch();

  const closePanel = useCallback(() => {
    setMeasuredResult(null);
    dispatch(setMeasureMode(null));
  }, [dispatch]);

  const guid = useAppSelector((state) => state.panorama.guid);
  const currentFieldId = useAppSelector(
    (state) => state.panorama.currentFieldDetails?.fid
  );
  const droneLat = useAppSelector(
    (state) => state.panorama.currentFieldDetails?.lat
  );
  const droneLng = useAppSelector(
    (state) => state.panorama.currentFieldDetails?.lng
  );
  const droneAltitude = useAppSelector(
    (state) => state.panorama.currentFieldDetails?.drone_altitude
  );
  const orientationAngle = useAppSelector(
    (state) => state.panorama.currentFieldDetails?.orientation_angle
  );

  // When mesasure mode turned on, reset previous measurement
  useEffect(() => {
    if (measureMode !== "ACTIVE") {
      closePanel();
    }
  }, [measureMode, closePanel]);

  useEffect(() => {
    const measureHandler = async (
      event: WindowEventMap["krpano__get_measure"]
    ) => {
      const measureItems = window.krpano
        .get<MeasurePoint, true>("global_measuring_points")
        .getArray();
      if (
        notNullNorUndefined(droneLat) &&
        notNullNorUndefined(droneLng) &&
        notNullNorUndefined(droneAltitude) &&
        notNullNorUndefined(currentFieldId) &&
        notNullNorUndefined(guid)
      ) {
        setMeasuredResult({
          message: "Calculating distance...",
        });
        // Correct ath values based on orientation angle
        const correctedItems = measureItems.map((item) => ({
          ath:
            notNullNorUndefined(item.ath) &&
            notNullNorUndefined(orientationAngle)
              ? item.ath + parseFloat(orientationAngle)
              : item.ath,
          atv: item.atv,
        }));
        const response = await MeasureApi.getMeasurement({
          drone_lat: droneLat,
          drone_lon: droneLng,
          drone_alt: droneAltitude,
          fieldId: currentFieldId,
          panoGuid: guid,
          point_list: correctedItems,
        });
        if (response.success && response.data) {
          setMeasuredResult({
            result: response.data.distance,
          });
          dispatch(setMeasureMode("ACTIVE"));
          // Delete measure items from krpano
          // Add points to krpano
          const measurementLine = addKrpanoHotspot(
            "measurement_line",
            {
              // Group
              group: "measurement_line",
            },
            "Measurement_Line"
          );
          // Create Measurement Line
          addKrpanoHotspotLayer(measurementLine, response.data.points);
        } else {
          console.warn("Error getting distance", response.errors);
          setMeasuredResult({
            message: `An error occurred loading distance${
              response.errors ? `: ${response.errors}` : "."
            }`,
          });
        }
      } else {
        console.warn("Possible missing data, check if any of these are null", {
          eventDetails: event.detail,
          droneLat,
          droneLng,
          droneAltitude,
          currentFieldId,
          guid,
        });
        setMeasuredResult({
          message: "Data missing to calculate distance. Please try again.",
        });
      }
    };
    window.addEventListener("krpano__get_measure", measureHandler);
    return () => {
      window.removeEventListener("krpano__get_measure", measureHandler);
    };
  }, [
    dispatch,
    currentFieldId,
    droneAltitude,
    droneLat,
    droneLng,
    guid,
    orientationAngle,
  ]);

  return [measuredResult, closePanel];
};
